import { ReactNode, useState } from "react";
import useSWR, { mutate } from "swr";
import useSWRMutation from "swr/mutation";
import { format } from "date-fns";
import { PencilIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Chip } from "@/components/badge/chip";
import { MarkdownTextarea } from "@/components/markdown-textarea";
import { visibleUnderApp } from "@/components/thread/post-filter";
import type { App } from "@/types/domain";
import { ListLoading } from "@/components/list/status-text";
import { delFetcher, postFetcher, putFetcher, swrKey, useAuthStore } from "@/lib/swr";
import { ProseMarkdown } from "@/components/prose-markdown";
import { DATE_TIME } from "@/lib/date";
import {
  CopyPostLinkButton,
  DeletePostButton,
  POST_CARD_CLASS,
  postAnchorId,
  useScrollToHashPost,
} from "@/components/thread/post-link";

// Shared thread stream (PRD / bug): chronological markdown posts +
// GitHub-issues style reply box. The entity-specific bits — endpoint, list
// query, create body, author header — are injected.

interface ThreadPostBase {
  id: string;
  content: string;
  /** Optional App tag (PRD dab8eb54); null = global post. */
  appId?: string | null;
  createdAt?: string;
  /** Set only when the post was edited — renders the "edited" hint. */
  updatedAt?: string | null;
  [key: string]: unknown;
}

// Sentinel for "global" in the app selects — Radix Select can't carry "".
const GLOBAL = "__global__";

// Composer / edit-mode app picker: 全局 or one of the PRD's apps. Only
// rendered when the thread has apps to choose from.
const AppPicker = ({
  apps,
  value,
  onChange,
}: {
  apps: App[];
  value: string | null;
  onChange: (appId: string | null) => void;
}) => (
  <Select
    value={value ?? GLOBAL}
    onValueChange={(v) => onChange(v === GLOBAL ? null : v)}
  >
    <SelectTrigger size="sm" className="w-36 text-xs" aria-label="Post app">
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value={GLOBAL}>全局</SelectItem>
      {apps.map((a) => (
        <SelectItem key={a.id} value={String(a.id)}>
          {a.name ?? a.id}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

// One thread post, GitHub-issue style: author header + markdown body, always
// expanded. Edits / deletes are written back into the stream by the parent
// (onSaved / onDeleted) — no refetch of the whole thread (WED-249).
const PostCard = <P extends ThreadPostBase>({
  post,
  endpoint,
  renderAuthor,
  apps,
  canPickApp,
  onSaved,
  onDeleted,
}: {
  post: P;
  endpoint: string;
  renderAuthor: (post: P) => ReactNode;
  apps: App[];
  canPickApp: boolean;
  onSaved: (post: P) => void;
  onDeleted: (id: string) => void;
}) => {
  const { trigger: del } = useSWRMutation(endpoint, delFetcher, {
    onSuccess: () => onDeleted(post.id),
  });
  // Inline edit: swap the markdown body for a textarea; the server restricts
  // editing to the author (or an admin) — a stray click just gets a 403 toast.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [draftApp, setDraftApp] = useState<string | null>(null);
  const postApp = post.appId
    ? apps.find((a) => String(a.id) === String(post.appId))
    : undefined;
  const { trigger: saveEdit, isMutating: saving } = useSWRMutation(
    `${endpoint}/${post.id}`,
    putFetcher<Partial<P>>,
    {
      onSuccess: (updated) => {
        setEditing(false);
        // The PUT echoes the revised post; keep author etc. from the row.
        onSaved({ ...post, ...updated });
      },
    }
  );
  const startEdit = () => {
    setDraft(post.content);
    setDraftApp(post.appId ? String(post.appId) : null);
    setEditing(true);
  };
  const time = post.createdAt
    ? format(new Date(post.createdAt), DATE_TIME)
    : null;
  return (
    <div id={postAnchorId(post.id)} className={POST_CARD_CLASS}>
      <div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-1.5">
        {renderAuthor(post)}
        {post.appId && (
          <Chip tone="blue" title="只关于这个 App">
            {postApp?.name ?? post.appId}
          </Chip>
        )}
        {time && (
          <span className="shrink-0 text-xs text-muted-foreground">{time}</span>
        )}
        {post.updatedAt && (
          <span className="shrink-0 text-xs text-muted-foreground/70">
            · edited
          </span>
        )}
        <CopyPostLinkButton postId={post.id} />
        <Button
          variant="ghost"
          size="icon"
          onClick={startEdit}
          className="h-6 w-6 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100"
          aria-label="Edit post"
          title="Edit"
        >
          <PencilIcon className="h-3.5 w-3.5" />
        </Button>
        <DeletePostButton onDelete={() => del(post.id)} />
      </div>
      <div className="px-3 py-2">
        {editing ? (
          <div className="flex flex-col gap-2">
            <MarkdownTextarea
              value={draft}
              onValueChange={setDraft}
              withPreview
              rows={8}
              className="font-mono text-xs leading-relaxed"
            />
            <div className="flex items-center gap-2">
              {canPickApp && apps.length > 0 && (
                <AppPicker apps={apps} value={draftApp} onChange={setDraftApp} />
              )}
              <Button
                size="sm"
                className="h-7"
                disabled={!draft.trim() || saving}
                onClick={() => saveEdit({ content: draft, appId: draftApp })}
              >
                {saving ? "Saving…" : "Save"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7"
                disabled={saving}
                onClick={() => setEditing(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <ProseMarkdown content={post.content} variant="post" />
        )}
      </div>
    </div>
  );
};

// No section title / empty-state copy (owner 2026-08-18): the stream speaks
// for itself; the only chrome is a right-aligned 发帖 button that opens the
// composer on demand.
export const ThreadSection = <P extends ThreadPostBase>({
  endpoint,
  query = {},
  createBody,
  renderAuthor,
  replyPlaceholder,
  prepend,
  apps = [],
  defaultAppId,
  canPickApp = true,
}: {
  /** PRD discussion collection, e.g. "/prds/WED-174/threads". */
  endpoint: string;
  /** Optional collection filters. The parent PRD is part of the endpoint. */
  query?: Record<string, unknown>;
  /** Body of the create POST for a reply (appId = optional App tag). */
  createBody: (content: string, appId: string | null) => Record<string, unknown>;
  /** Author header cells (name, extra badges…). */
  renderAuthor: (post: P) => ReactNode;
  replyPlaceholder: string;
  /** Extra items rendered at the top of the stream (e.g. PRD reports). */
  prepend?: ReactNode;
  /** Apps a post may be tagged with (the PRD's relations). Drives the
   *  filter row and the composer picker; empty = feature hidden. */
  apps?: App[];
  /** Composer tag (and, when the tag is pickable, initial filter), e.g. the
   *  app the page was opened from. */
  defaultAppId?: string | null;
  /** false = the tag is fixed to defaultAppId (work-item path): pickers are
   *  hidden and the stream starts unfiltered, so other apps' posts on the
   *  same document stay visible (WED-186). */
  canPickApp?: boolean;
}) => {
  // keepPreviousData: a query change (e.g. app filter) keeps the current posts
  // on screen instead of flashing the loader.
  const key = swrKey(endpoint, query);
  const { data: posts, isLoading } = useSWR<P[]>(key, { keepPreviousData: true });
  const me = useAuthStore((store) => store.auth?.user);
  // Write-back into this stream's cache key; the server response is the
  // truth for the row, so no revalidation (WED-249).
  const setPosts = (update: (posts: P[]) => P[]) =>
    mutate<P[]>(key, (cur) => update(cur ?? []), { revalidate: false });

  const [content, setContent] = useState("");
  // App filter (PRD dab8eb54): tagged posts are about one app, untagged are
  // global and show under every app. Composer defaults to the same app so a
  // reply made while filtered stays in view.
  const [filterApp, setFilterApp] = useState<string | null>(
    canPickApp && defaultAppId ? String(defaultAppId) : null
  );
  const [composeApp, setComposeApp] = useState<string | null>(
    defaultAppId ? String(defaultAppId) : null
  );
  const shown = visibleUnderApp(posts ?? [], filterApp);
  const countFor = (appId: string | null) =>
    visibleUnderApp(posts ?? [], appId).length;
  useScrollToHashPost(!!posts?.length);

  const { trigger: create, isMutating: creating } = useSWRMutation(
    endpoint,
    postFetcher<P>,
    {
      onSuccess: (post) => {
        // The POST returns the bare row (no author relation): the author is me.
        setPosts((cur) => [...cur, { ...post, author: post.author ?? me }]);
        setContent("");
      },
    }
  );

  return (
    <section className="mt-8 space-y-2">
      {prepend}
      {apps.length > 0 && !!posts?.length && (
        <div className="flex flex-wrap items-center gap-1">
          <Button
            variant={filterApp === null ? "default" : "outline"}
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => setFilterApp(null)}
          >
            全部 ({countFor(null)})
          </Button>
          {apps.map((a) => (
            <Button
              key={a.id}
              variant={filterApp === String(a.id) ? "default" : "outline"}
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => setFilterApp(String(a.id))}
            >
              {a.name ?? a.id} ({countFor(String(a.id))})
            </Button>
          ))}
        </div>
      )}
      {isLoading && !posts ? (
        <ListLoading />
      ) : shown.length ? (
        <div className="flex flex-col gap-2">
          {shown.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              endpoint={endpoint}
              renderAuthor={renderAuthor}
              apps={apps}
              canPickApp={canPickApp}
              onSaved={(saved) =>
                setPosts((cur) => cur.map((p) => (p.id === saved.id ? saved : p)))
              }
              onDeleted={(id) => setPosts((cur) => cur.filter((p) => p.id !== id))}
            />
          ))}
        </div>
      ) : null}

      {/* Always-open composer; the submit sits on the right (owner). */}
      <div className="mt-3 flex flex-col gap-2 rounded-md border p-3">
        <MarkdownTextarea
          value={content}
          onValueChange={setContent}
          withPreview
          placeholder={replyPlaceholder}
          rows={4}
          className="font-mono text-xs leading-relaxed"
        />
        <div className="flex items-center justify-end gap-2">
          {canPickApp && apps.length > 0 && (
            <AppPicker apps={apps} value={composeApp} onChange={setComposeApp} />
          )}
          <Button
            size="sm"
            className="h-7"
            disabled={!content.trim() || creating}
            onClick={() => create(createBody(content, composeApp))}
          >
            {creating ? "Posting…" : "Post"}
          </Button>
        </div>
      </div>
    </section>
  );
};
